import { useEffect, useMemo, useRef, useState } from "react";
import FixedPage from "src/components/Layout/FixedPage";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import { lang } from "src/language/Strings";
import { ActiveFileModal } from "./FileInput";
import useFileMutation from "src/api/hooks/useFileMutation";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/api/queryKeys";
import { useParams } from "react-router-dom";
import { Spinner } from "src/components/LoadingSpinner";

interface ImageGalleryProps {
  images: SasFileResult[] | undefined;
  id?: string;
  maxHeight?: string;
  editMode?: boolean;
}



export function ImageGallery({
  images,
  id = "popup-image-gallery",
  maxHeight = "h-40",
  editMode = false,
}: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullSize, setIsFullSize] = useState(false);
  const [editedFile, setEditedFile] = useState<SasFileResult | null>(null);
  const [newFile, setNewFile] = useState<SasFileResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { deviationId, inspectionId, companyId } = useParams();
  const filteredImages = useMemo(() => {
    if (!images || images.length === 0) return [];
    return images.filter((i) => i.files?.length && i.files?.length > 0);
  }, [images]);
  const queryClient = useQueryClient();
  const fileMutation = useFileMutation();
  useEffect(() => {
    if (fileMutation.create.status === "success") {
      setEditedFile(null);
      setNewFile(null);
      fileMutation.invalidate();
      queryClient.invalidateQueries({
        queryKey: queryKeys.selectedVariation(inspectionId || deviationId! || "")
        
      });
    }
  }, [fileMutation.create.status]);

  const currentImage = useMemo(() => {
    if (filteredImages.length === 0) return null;
    return filteredImages[currentIndex];
  }, [filteredImages, currentIndex]);
  
  const imageUrl = useMemo(() => currentImage?.files?.[0] || "", [currentImage]);

  if (filteredImages.length === 0) return null;

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  const openFullSize = (index: number) => {
    setCurrentIndex(index);
    setIsFullSize(true);
  };

  const navigateFullSize = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentIndex(
        (prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1)
      );
    } else {
      setCurrentIndex(
        (prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1)
      );
    }
  };

  const handleAddImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const newFileObject: SasFileResult = {
        file: file,
        fileName: file.name,
        comment: "",
        depiction: "",
        action: "",
        companyId: Number(companyId),
        deviationId: deviationId ? Number(deviationId) : inspectionId ? Number(inspectionId) : undefined,
        fileType: FileType.Image,
        files: [],
      };
      setNewFile(newFileObject);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  return (
    <>
    <div className="flex flex-col gap-2 w-full">
      <div
        id={`${id}-container`}
        className={`relative ${maxHeight} h-40 w-full max-w-full bg-Grey2 rounded-lg overflow-hidden border border-Grey4 group`}
      >
        {/* Main Image */}
        <div className="relative w-full h-full max-w-full flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={`Image ${currentIndex + 1}`}
            className={`${maxHeight} max-w-full w-auto h-full object-contain cursor-pointer transition-opacity duration-200`}
            onClick={() => openFullSize(currentIndex)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (currentImage?.files?.[0] && target.src !== currentImage.files[0]) {
                target.src = currentImage.files[0];
              }
            }}
          />

          {/* Navigation Arrows - Show always*/}
          {filteredImages.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-2 rounded-full shadow-lg transition-opacity duration-200 z-10"
                aria-label="Previous image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-2 rounded-full shadow-lg transition-opacity duration-200 z-10"
                aria-label="Next image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Image Counter */}
          {filteredImages.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-Grey9/80 text-White px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
              {currentIndex + 1} / {filteredImages.length}
            </div>
          )}

          {/* Thumbnail Strip (if multiple images) */}
          {filteredImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-Grey9/90 to-transparent p-2 max-w-full overflow-hidden">
              <div className="flex gap-1.5 overflow-x-auto justify-center max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {filteredImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all duration-200 ${
                      index === currentIndex
                        ? "border-PrimaryGreen shadow-md scale-110"
                        : "border-Grey4 opacity-70 hover:opacity-100 hover:border-Grey7"
                    }`}
                  >
                    <img
                      src={img.files?.[0] || ""}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // If thumbnail fails to load, fallback to the main image
                        if (img.files?.[0] && target.src !== img.files?.[0]) {
                          target.src = img.files?.[0];
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
      </div>

      {/* Image Information Display */}
      {currentImage && (currentImage.depiction || currentImage.comment || currentImage.action) && (
        <div className="w-full bg-Grey1 rounded-lg border border-Grey4 p-3 space-y-2">
          {currentImage.depiction && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.objectType}:</div>
              <div className="text-sm text-Grey9">{currentImage.depiction}</div>
            </div>
          )}
          {currentImage.comment && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.description}:</div>
              <div className="text-sm text-Grey9">{currentImage.comment}</div>
            </div>
          )}
          {currentImage.action && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.action}:</div>
              <div className="text-sm text-Grey9">{currentImage.action}</div>
            </div>
          )}
        </div>
      )}

      <div id="image-edit-button-container" className="flex flex-row gap-2 w-full justify-center items-center" >
      <button className="rounded-xl p-2 border-2 border-Grey4 bg-Grey1 text-PrimaryRed">  {lang.deleteImage} </button>
        <button className="rounded-xl p-2 border-2 border-Grey4 bg-Grey1" onClick={() => setEditedFile(currentImage)}>  {lang.editImage} </button>
        <button className="rounded-xl p-2 border-2 border-Grey4 bg-Grey1" onClick={handleAddImageClick}>  {lang.addImage} </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      </div>
      {editedFile && 
      <ActiveFileModal
        activeFile={editedFile}
        setActiveFile={(file) => setEditedFile(file)}
        companyFiles={filteredImages}
        onClick={async (file) => {
            fileMutation.create.mutate(file);
        }}
      />
}
      {newFile && 
      <ActiveFileModal
        activeFile={newFile}
        setActiveFile={(file) => setNewFile(file)}
        companyFiles={filteredImages}
        onClick={async (file) => {
            fileMutation.create.mutate(file);
        }}
      />
} 
      {/* Full Size Modal */}
      {isFullSize && (
        <FixedPage onOutsideClick={() => setIsFullSize(false)} fullScreen={true}>
          <div className="relative w-full h-full flex items-center justify-center  p-4">
            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullSize("prev")}}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-3 rounded-full shadow-lg z-20 transition-all"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullSize("next")}}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-3 rounded-full shadow-lg z-20 transition-all"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-Grey9/80 text-White px-4 py-2 rounded-full text-sm font-semibold shadow-lg z-20">
                  {currentIndex + 1} / {filteredImages.length}
                </div>
              </>
            )}

            <img
              src={filteredImages[currentIndex]?.files?.[0] || ""}
              alt={`Full size image ${currentIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
              onClick={() => setIsFullSize(false)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                const fullSizeImage = filteredImages[currentIndex];
                if (fullSizeImage?.files?.[0] && target.src !==  fullSizeImage.files[0]) {
                  target.src = fullSizeImage.files[0];
                }
              }}
            />
          </div>
        </FixedPage>
      )}
      {fileMutation.create.isPending && <Spinner />}
    </>
  );
}

